import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  AlertTriangle,
  CheckCircle2,
  Loader2,
  Plus,
  X,
} from "lucide-react";
import { AdminGate } from "@/components/admin/AdminGate";
import { AdminLayout } from "@/components/admin/AdminLayout";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button, buttonVariants } from "@/components/ui/button";
import { api } from "@/lib/api";

type StorefrontDiscoveryStatus = "none" | "discovering" | "done" | "failed";

type CustomerBrand = {
  id: string;
  name: string;
  storefrontUrl: string | null;
  storefrontDiscoveryStatus: StorefrontDiscoveryStatus;
  storefrontDiscoveryAt: string | null;
  asinCount: number;
};

type CustomerRow = {
  id: string;
  email: string;
  organizationName: string;
  createdAt: string;
  brands: CustomerBrand[];
};

const ROW_CONTROL_SELECTOR =
  "a, button, input, select, textarea, label, summary, [contenteditable], [role=button], [role=link], [data-row-action]";

function rowEventStartedInControl(event: React.MouseEvent<HTMLTableRowElement>) {
  return (
    event.target instanceof Element &&
    event.target.closest(ROW_CONTROL_SELECTOR) !== null
  );
}

function BrandsSummary({ customer }: { customer: CustomerRow }) {
  const brandCount = customer.brands.length;
  const asinCount = customer.brands.reduce((sum, brand) => sum + brand.asinCount, 0);
  const syncingCount = customer.brands.filter(
    (brand) => brand.storefrontDiscoveryStatus === "discovering"
  ).length;
  const failedCount = customer.brands.filter(
    (brand) => brand.storefrontDiscoveryStatus === "failed"
  ).length;
  const names = customer.brands.slice(0, 2).map((brand) => brand.name);

  return (
    <div className="min-w-[220px]">
      <div className="text-sm font-medium">
        {brandCount} managed {brandCount === 1 ? "brand" : "brands"} · {asinCount} ASINs
      </div>
      <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
        <span>
          {names.length > 0
            ? `${names.join(", ")}${brandCount > names.length ? ` +${brandCount - names.length}` : ""}`
            : "No brands"}
        </span>
        {syncingCount > 0 && <span className="text-sky-600">{syncingCount} syncing</span>}
        {failedCount > 0 && <span className="text-red-600">{failedCount} failed</span>}
      </div>
      <Link
        to={`/admin/customers/${customer.id}`}
        className="mt-2 inline-flex text-xs font-medium text-foreground underline-offset-4 hover:underline"
      >
        Manage brands →
      </Link>
    </div>
  );
}

function Inner() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const statusFilter = params.get("status") ?? "all";
  const [customers, setCustomers] = useState<CustomerRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [showCreate, setShowCreate] = useState(false);

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    setCustomers(null);
    const query = new URLSearchParams();
    if (statusFilter !== "all") query.set("status", statusFilter);
    api<{ customers: CustomerRow[] }>(
      `/admin/customers${query.size ? `?${query}` : ""}`
    ).then((res) => {
      if (cancelled) return;
      if (!res.ok) {
        setError(res.error ?? "Failed to load customers.");
        return;
      }
      setCustomers(res.data?.customers ?? []);
    });
    return () => {
      cancelled = true;
    };
  }, [reloadKey, statusFilter]);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle>Customers</CardTitle>
            <CardDescription>
              Organizations and agencies with one or more managed Amazon brands.
            </CardDescription>
          </div>
          <Button
            size="sm"
            onClick={() => setShowCreate(true)}
            className="shrink-0"
          >
            <Plus className="h-4 w-4" /> Create managed account
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="mb-4 flex flex-wrap items-center gap-2">
          {[
            { value: "all", label: "All" },
            { value: "none", label: "Not started" },
            { value: "invite_sent", label: "Invite sent" },
            { value: "verified", label: "Verified" },
          ].map((filter) => (
            <Button
              key={filter.value}
              type="button"
              size="sm"
              variant={statusFilter === filter.value ? "default" : "outline"}
              onClick={() => {
                if (filter.value === "all") setParams({});
                else setParams({ status: filter.value });
              }}
            >
              {filter.label}
            </Button>
          ))}
        </div>
        {error && <p className="text-sm text-destructive">{error}</p>}
        {!customers && !error && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        )}
        {customers && customers.length === 0 && !error && (
          <p className="text-sm text-muted-foreground">No customers yet.</p>
        )}
        {customers && customers.length > 0 && (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Organization</TableHead>
                <TableHead>Account email</TableHead>
                <TableHead>Managed brands</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {customers.map((c) => {
                const href = `/admin/customers/${c.id}`;

                return (
                  <TableRow
                    key={c.id}
                    onClick={(event) => {
                      if (
                        event.defaultPrevented ||
                        rowEventStartedInControl(event) ||
                        event.altKey
                      ) {
                        return;
                      }

                      if (event.metaKey || event.ctrlKey || event.shiftKey) {
                        window.open(href, "_blank", "noopener,noreferrer");
                        return;
                      }

                      navigate(href);
                    }}
                    onAuxClick={(event) => {
                      if (
                        event.defaultPrevented ||
                        event.button !== 1 ||
                        rowEventStartedInControl(event)
                      ) {
                        return;
                      }

                      event.preventDefault();
                      window.open(href, "_blank", "noopener,noreferrer");
                    }}
                    className="cursor-pointer focus-within:bg-muted/50"
                  >
                    <TableCell className="font-medium">
                      <Link
                        to={href}
                        className="underline-offset-4 hover:underline"
                      >
                        {c.organizationName}
                      </Link>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {c.email}
                    </TableCell>
                    <TableCell>
                      <BrandsSummary customer={c} />
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {new Date(c.createdAt).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-right">
                      <Link
                        to={href}
                        className={buttonVariants({ size: "sm", variant: "outline" })}
                      >
                        Open
                      </Link>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>

      {showCreate && (
        <CreateManagedModal
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            setShowCreate(false);
            reload();
          }}
        />
      )}
    </Card>
  );
}

function CreateManagedModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}) {
  const [organizationName, setOrganizationName] = useState("");
  const [brandName, setBrandName] = useState("");
  const [allowNameMatch, setAllowNameMatch] = useState(false);
  const [mode, setMode] = useState<"asins" | "storefront">("asins");
  const [asinsRaw, setAsinsRaw] = useState("");
  const [storefrontUrl, setStorefrontUrl] = useState("");
  const [customerContactEmail, setCustomerContactEmail] = useState("");
  const [operatorEmail, setOperatorEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<{
    customerId: string;
    brandId: string;
  } | null>(null);
  const normalizedOrganizationName = organizationName.trim().toLowerCase();
  const normalizedBrandName = brandName.trim().toLowerCase();
  const namesMatch =
    normalizedOrganizationName.length > 0 &&
    normalizedOrganizationName === normalizedBrandName;

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);

    const body: Record<string, unknown> = {
      organizationName: organizationName.trim(),
      brandName: brandName.trim(),
      customerContactEmail: customerContactEmail.trim(),
      operatorEmail: operatorEmail.trim(),
    };
    if (namesMatch && allowNameMatch) {
      body.allowOrganizationBrandNameMatch = true;
    }
    if (mode === "asins") {
      const asins = asinsRaw
        .split(/[\s,]+/)
        .map((s) => s.trim().toUpperCase())
        .filter(Boolean);
      if (asins.length === 0) {
        setError("Paste at least one ASIN.");
        setSubmitting(false);
        return;
      }
      body.asins = asins;
    } else {
      body.storefrontUrl = storefrontUrl.trim();
    }

    const res = await api<{
      ok: true;
      customerId: string;
      brandId: string;
      workosOrgId: string;
    }>("/admin/customers/managed-create", {
      method: "POST",
      body: JSON.stringify(body),
    });
    setSubmitting(false);
    if (!res.ok || !res.data) {
      setError(res.error ?? "Couldn't create the managed account.");
      return;
    }
    setSuccess({ customerId: res.data.customerId, brandId: res.data.brandId });
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4"
      onClick={onClose}
    >
      <div
        className="max-h-[calc(100vh-2rem)] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-6 shadow-xl dark:bg-slate-900"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
            Create managed account
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-1 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {success ? (
          <div className="mt-6 rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-200">
            <div className="flex items-center gap-2 font-semibold">
              <CheckCircle2 className="h-4 w-4" /> Account created
            </div>
            <p className="mt-2">
              Brand <span className="font-mono">{success.brandId}</span> under
              organization <span className="font-mono">{success.customerId}</span>.
              The operator can now switch into this org from the user-menu
              dropdown.
            </p>
            <div className="mt-4 flex gap-3">
              <Button size="sm" onClick={onCreated}>
                Done
              </Button>
            </div>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="mt-4 space-y-4">
            <p className="text-xs text-muted-foreground">
              Reputely creates an organization account and an initial Amazon
              brand under it. For agencies, use the agency name as the
              organization and the client/product line as the brand.
            </p>

            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
                Organization name
              </label>
              <input
                type="text"
                required
                value={organizationName}
                onChange={(e) => setOrganizationName(e.target.value)}
                placeholder="PLTFRM"
                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-900/10 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
              />
              <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                The WorkOS organization and account switcher label.
              </p>
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
                First managed brand
              </label>
              <input
                type="text"
                required
                value={brandName}
                onChange={(e) => setBrandName(e.target.value)}
                placeholder="SKINN"
                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-900/10 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
              />
              <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                The Amazon brand whose products will be monitored.
              </p>
            </div>

            {namesMatch && (
              <label className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-200">
                <input
                  type="checkbox"
                  checked={allowNameMatch}
                  onChange={(e) => setAllowNameMatch(e.target.checked)}
                  className="mt-0.5"
                />
                <span>
                  Organization and brand names match. Confirm this is a direct
                  brand account, not an agency managing separate brands.
                </span>
              </label>
            )}
            <div className="flex gap-2 text-xs">
              <button
                type="button"
                onClick={() => setMode("asins")}
                className={`rounded-full px-3 py-1 ${
                  mode === "asins"
                    ? "bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900"
                    : "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300"
                }`}
              >
                ASIN list
              </button>
              <button
                type="button"
                onClick={() => setMode("storefront")}
                className={`rounded-full px-3 py-1 ${
                  mode === "storefront"
                    ? "bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900"
                    : "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300"
                }`}
              >
                Storefront URL
              </button>
            </div>

            {mode === "asins" ? (
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
                  ASINs
                </label>
                <textarea
                  required
                  rows={4}
                  value={asinsRaw}
                  onChange={(e) => setAsinsRaw(e.target.value)}
                  placeholder={"B0CFPJYX7P\nB0DMTNDHMY\n…"}
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 font-mono text-sm text-slate-900 placeholder:text-slate-400 focus:border-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-900/10 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                />
                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                  Comma- or newline-separated. Routes through the auth-first
                  scrape pipeline — manual workers pick up the jobs.
                </p>
              </div>
            ) : (
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
                  Amazon storefront URL
                </label>
                <input
                  type="url"
                  required
                  value={storefrontUrl}
                  onChange={(e) => setStorefrontUrl(e.target.value)}
                  placeholder="https://www.amazon.com/stores/YourBrand/page/…"
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:border-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-900/10 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                />
                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                  We'll scan the storefront for featured products.
                </p>
              </div>
            )}

            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
                Customer contact email (placeholder)
              </label>
              <input
                type="email"
                required
                value={customerContactEmail}
                onChange={(e) => setCustomerContactEmail(e.target.value)}
                placeholder="trial@brand.example"
                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:border-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-900/10 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
              />
              <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                Replaced by the new owner's email at ownership transfer.
              </p>
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
                Operator email
              </label>
              <input
                type="email"
                required
                value={operatorEmail}
                onChange={(e) => setOperatorEmail(e.target.value)}
                placeholder="you@reputely.co"
                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:border-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-900/10 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
              />
              <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                Must already have a Reputely user. Will be added as
                owner-member.
              </p>
            </div>

            {error && (
              <p
                className="flex items-center gap-2 text-sm text-red-600 dark:text-red-400"
                role="alert"
              >
                <AlertTriangle className="h-4 w-4" /> {error}
              </p>
            )}

            <div className="flex items-center justify-end gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                {submitting ? "Creating…" : "Create"}
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export default function AdminCustomers() {
  return (
    <AdminGate>
      <AdminLayout>
        <Inner />
      </AdminLayout>
    </AdminGate>
  );
}
